import type { Aviso, Espelho, EspelhoCarga, EspelhoDia, EspelhoFeriado, Ocorrencia, TipoDia } from "@/api/escala.service";
import { rotuloDaRegra } from "@/components/escala/tiposDia";
import { ultimoDiaDoMes } from "@/components/escala/DiaDoMesField";
import { blocoDoDia, formatarBloco, minutosDoBloco, motivoCargaDoDia } from "@/components/escala/cargaHoraria";

// Letra da semana no cabeçalho, domingo primeiro: D S T Q Q S S.
const LETRAS_SEMANA = ["D", "S", "T", "Q", "Q", "S", "S"];

export interface DiaColuna {
  dia: number;
  iso: string;
  letra: string;
  domingo: boolean;
  feriado?: string;
  // Só feriado com loja fechada — usado pra não marcar "abaixo do mínimo" no rodapé nesses dias
  // (R51: com a loja fechada ninguém trabalha mesmo, isso não é uma ocorrência).
  fechaLoja?: boolean;
}

export interface TotaisColaborador {
  trabalhados: number;
  folgas: number;
  // Horas trabalhadas no mês, sem o intervalo, em minutos; só a escala com carga soma.
  minutos: number;
}

export interface TotaisDia {
  trabalhando: number;
  folgando: number;
  compensando: number;
}

export interface CelulaGrade {
  tipo: TipoDia;
  // O tipo, a hora cheia da entrada ("07") ou "?".
  rotulo: string;
  // Entrada completa ("07:30"): é o que a grade da tela mostra no T.
  entrada?: string;
  // Cor da carga aplicada no dia, como foi cadastrada; sem ela é "Padrão".
  cor?: string;
  // Carga e horário do dia, para a dica.
  horario?: string;
  semHorario?: boolean;
  alterado?: boolean;
  // Carga trocada à mão no dia: o "*" da impressão.
  manual?: boolean;
}

// Linha de total de uma carga no rodapé.
export interface LinhaCarga {
  id: string;
  descricao: string;
  cor?: string;
  // Quem trabalha com a carga em cada dia do mês (índice 0 = dia 1), já com as trocas manuais.
  porDia: number[];
}

// Dias do mês anterior que antecedem o dia 1: só consulta, fora dos totais.
export interface HistoricoEspelho {
  colunas: DiaColuna[];
  // colaborador_id -> data (ISO) -> célula
  celulas: Map<string, Map<string, CelulaGrade>>;
}

export interface GradeEspelho {
  colunas: DiaColuna[];
  historico: HistoricoEspelho;
  // colaborador_id -> dia do mês (1-based) -> célula
  celulas: Map<string, Map<number, CelulaGrade>>;
  porColaborador: Map<string, TotaisColaborador>;
  porDia: TotaisDia[];
  // Só as cargas aplicadas em algum dia do mês, por descrição.
  cargas: LinhaCarga[];
}

function isoDoDia(ano: number, mes: number, dia: number) {
  return `${ano}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
}

// FA conta como folga: pra quem olha a grade é gente fora da loja, igual F.
const contaComoFolga = (tipo: TipoDia) => tipo === "F" || tipo === "FA";

// Ciclo da edição manual: T → F → C → T. Qualquer outro tipo não responde ao
// clique (vem do cadastro de ausência ou de folga compensatória).
export function proximoTipo(tipo: TipoDia): TipoDia | null {
  if (tipo === "T") return "F";
  if (tipo === "F") return "C";
  if (tipo === "C") return "T";
  return null;
}

// Uma coluna por dia do mês, com domingo e feriado marcados.
export function colunasDoMes(ano: number, mes: number, feriados: EspelhoFeriado[] = []): DiaColuna[] {
  const nomes = new Map(feriados.map((f) => [f.data, f.nome]));
  const fechaLoja = new Set(feriados.filter((f) => f.fecha_loja).map((f) => f.data));
  return Array.from({ length: ultimoDiaDoMes(ano, mes) }, (_, i) => {
    const dia = i + 1;
    const iso = isoDoDia(ano, mes, dia);
    const semana = new Date(ano, mes - 1, dia).getDay();
    return {
      dia,
      iso,
      letra: LETRAS_SEMANA[semana]!,
      domingo: semana === 0,
      feriado: nomes.get(iso),
      fechaLoja: fechaLoja.has(iso),
    };
  });
}

// Em escala com carga, o T mostra a entrada na cor da carga do dia ("07:30", na tela e na
// impressão), e o T sem horário é o "?". O horário é a cópia gravada no dia, que já traz as trocas manuais.
export function celulaDoDia(d: EspelhoDia, comCarga: boolean, cargas: ReadonlyMap<string, EspelhoCarga>): CelulaGrade {
  const alterado = !!d.alterado;
  if (d.tipo !== "T" || !comCarga) return { tipo: d.tipo, rotulo: d.tipo, alterado };
  if (!d.horario?.entrada) return { tipo: "T", rotulo: "?", horario: "Sem horário", semHorario: true, alterado };
  const carga = d.carga_horaria_id ? cargas.get(d.carga_horaria_id) : undefined;
  return {
    tipo: "T",
    rotulo: d.horario.entrada.slice(0, 2),
    entrada: d.horario.entrada,
    cor: carga?.cor,
    horario: carga ? `${carga.descricao} — ${formatarBloco(d.horario)}` : formatarBloco(d.horario),
    alterado,
    manual: !!d.manual,
  };
}

// O histórico mostra a hora como o mês. Só vem de gerada aprovada, que não tem "?": ali, o T sem
// horário é de escala sem carga e fica "T". Fica sem marca: o servidor manda só a carga trocada, sem
// o tipo mudado à mão.
function montarHistorico(espelho: Espelho, comCarga: boolean, cargas: ReadonlyMap<string, EspelhoCarga>): HistoricoEspelho {
  const celulas = new Map<string, Map<string, CelulaGrade>>();
  const datas = new Set<string>();
  for (const d of espelho.historico ?? []) {
    const linha = celulas.get(d.colaborador_id) ?? new Map<string, CelulaGrade>();
    linha.set(d.data, { ...celulaDoDia(d, comCarga && !!d.horario?.entrada, cargas), alterado: false, manual: false });
    celulas.set(d.colaborador_id, linha);
    datas.add(d.data);
  }
  const colunas = [...datas].sort().map((iso) => {
    const [ano, mes, dia] = iso.split("-").map(Number) as [number, number, number];
    const semana = new Date(ano, mes - 1, dia).getDay();
    return { dia, iso, letra: LETRAS_SEMANA[semana]!, domingo: semana === 0 };
  });
  return { colunas, celulas };
}

export function montarGrade(espelho: Espelho): GradeEspelho {
  const { ano, mes } = espelho.gerada;
  const colunas = colunasDoMes(ano, mes, espelho.feriados);
  const diaPorIso = new Map(colunas.map((c) => [c.iso, c.dia]));
  const cargas = new Map((espelho.cargas ?? []).map((c) => [c.id, c]));
  const comCarga = !!espelho.gerada.com_carga_horaria;

  const celulas = new Map<string, Map<number, CelulaGrade>>();
  const porColaborador = new Map<string, TotaisColaborador>();
  const porDia: TotaisDia[] = colunas.map(() => ({ trabalhando: 0, folgando: 0, compensando: 0 }));
  const porCarga = new Map<string, number[]>();

  for (const c of espelho.colaboradores) {
    celulas.set(c.id, new Map());
    porColaborador.set(c.id, { trabalhados: 0, folgas: 0, minutos: 0 });
  }

  for (const d of espelho.dias) {
    const linha = celulas.get(d.colaborador_id);
    const dia = diaPorIso.get(d.data);
    if (!linha || !dia) continue;
    linha.set(dia, celulaDoDia(d, comCarga, cargas));

    const totalColab = porColaborador.get(d.colaborador_id)!;
    const totalDia = porDia[dia - 1]!;
    if (d.tipo === "T") {
      totalColab.trabalhados++;
      totalDia.trabalhando++;
      if (comCarga && d.horario?.entrada) totalColab.minutos += minutosDoBloco(d.horario);
      // O "?" não conta para carga nenhuma.
      if (d.carga_horaria_id && d.horario?.entrada && cargas.has(d.carga_horaria_id)) {
        const contagem = porCarga.get(d.carga_horaria_id) ?? colunas.map(() => 0);
        contagem[dia - 1]!++;
        porCarga.set(d.carga_horaria_id, contagem);
      }
    } else if (contaComoFolga(d.tipo)) {
      totalColab.folgas++;
      totalDia.folgando++;
    } else if (d.tipo === "C") {
      totalDia.compensando++;
    }
  }

  const linhasCarga = [...porCarga]
    .map(([id, contagem]) => {
      const c = cargas.get(id)!;
      return { id, descricao: c.descricao, cor: c.cor, porDia: contagem };
    })
    .sort((a, b) => a.descricao.localeCompare(b.descricao, "pt-BR"));

  return {
    colunas,
    historico: montarHistorico(espelho, comCarga, cargas),
    celulas,
    porColaborador,
    porDia,
    cargas: linhasCarga,
  };
}

const mesmaCelula = (a: CelulaGrade, b?: CelulaGrade) =>
  !!b &&
  a.tipo === b.tipo &&
  a.rotulo === b.rotulo &&
  a.entrada === b.entrada &&
  a.cor === b.cor &&
  a.horario === b.horario &&
  a.semHorario === b.semHorario &&
  a.alterado === b.alterado &&
  a.manual === b.manual;

const mesmasCelulas = <K,>(a: Map<K, CelulaGrade>, b: Map<K, CelulaGrade>) =>
  a.size === b.size && [...a].every(([chave, celula]) => mesmaCelula(celula, b.get(chave)));

const mesmasColunas = (a: DiaColuna[], b: DiaColuna[]) =>
  a.length === b.length &&
  a.every((c, i) => c.iso === b[i]!.iso && c.feriado === b[i]!.feriado && c.fechaLoja === b[i]!.fechaLoja);

// Depois de uma edição, reaproveita da grade anterior tudo o que não mudou
// (colunas, células e totais de cada colaborador), para o memo das linhas
// re-renderizar só a linha editada.
export function estabilizarGrade(nova: GradeEspelho, anterior?: GradeEspelho): GradeEspelho {
  if (!anterior) return nova;

  const celulas = new Map<string, Map<number, CelulaGrade>>();
  const porColaborador = new Map<string, TotaisColaborador>();
  for (const [id, linha] of nova.celulas) {
    const antes = anterior.celulas.get(id);
    const igual = antes !== undefined && mesmasCelulas(linha, antes);
    celulas.set(id, igual ? antes : linha);
    porColaborador.set(id, igual ? anterior.porColaborador.get(id)! : nova.porColaborador.get(id)!);
  }
  // O histórico não muda com a edição: reaproveita o da grade anterior pra não refazer as linhas.
  const histNovo = nova.historico;
  const histAntes = anterior.historico;
  const historicoIgual =
    mesmasColunas(histNovo.colunas, histAntes.colunas) &&
    histNovo.celulas.size === histAntes.celulas.size &&
    [...histNovo.celulas].every(([id, linha]) => {
      const antes = histAntes.celulas.get(id);
      return antes !== undefined && mesmasCelulas(linha, antes);
    });
  return {
    ...nova,
    historico: historicoIgual ? histAntes : histNovo,
    colunas: mesmasColunas(nova.colunas, anterior.colunas) ? anterior.colunas : nova.colunas,
    celulas,
    porColaborador,
  };
}

// O que o espelho sabe para explicar um "?": a carga padrão de cada colaborador, as cargas, os
// feriados e se a grade aceita edição, de que depende a dica de como resolver.
export type ContextoSemHorario = Pick<Espelho, "colaboradores" | "cargas" | "feriados"> & { editavel?: boolean };

const REGRA_SEM_HORARIO = "DIA_SEM_HORARIO";

// Por que o T do dia ficou sem horário, pela regra de horarioPadrao (cargas_da_gerada.go): a carga
// padrão precisa ser da escala e ter o bloco do tipo do dia, ativa ou não. O "?" não guarda qual carga
// o deixou sem horário, então a explicação é pelo cadastro de hoje; se a padrão de hoje já serve ao
// dia (mudou depois, num rascunho que o reflexo não alcança), diz isso.
export function motivoSemHorario(contexto: ContextoSemHorario, colaboradorId: string, iso: string): string {
  const padraoId = contexto.colaboradores.find((c) => c.id === colaboradorId)?.carga_horaria_id;
  if (!padraoId) return "o colaborador não tem carga horária padrão";
  const carga = contexto.cargas?.find((c) => c.id === padraoId);
  if (!carga) return "o dia ficou sem horário definido";
  const bloco = blocoDoDia(iso, contexto.feriados);
  // A padrão vale mesmo inativada: o próprio id como o do dia pula a checagem de ativa.
  const motivo = motivoCargaDoDia(carga, bloco, carga.id);
  if (!motivo) return `a carga padrão "${carga.descricao}" já tem horário para este dia, mas ele não foi aplicado`;
  const feriado = bloco === "feriado" && carga.da_escala ? contexto.feriados.find((f) => f.data === iso) : undefined;
  return feriado ? `${motivo} (${feriado.nome})` : motivo;
}

// colaborador_id -> data (ISO) -> regras violadas naquele dia, já em texto. Com o contexto, o dia sem
// horário diz o motivo no lugar do nome da regra: a dica da célula já diz "Sem horário".
export type InfracoesPorColaborador = Map<string, Map<string, string>>;

export function infracoesDasOcorrencias(ocorrencias: Ocorrencia[], contexto?: ContextoSemHorario): InfracoesPorColaborador {
  const regras = new Map<string, Map<string, string[]>>();
  for (const o of ocorrencias) {
    const porData = regras.get(o.colaborador_id) ?? new Map<string, string[]>();
    for (const data of o.datas) {
      const texto =
        o.regra === REGRA_SEM_HORARIO && contexto ? motivoSemHorario(contexto, o.colaborador_id, data) : rotuloDaRegra(o.regra);
      porData.set(data, [...(porData.get(data) ?? []), texto]);
    }
    regras.set(o.colaborador_id, porData);
  }
  return new Map(
    [...regras].map(([id, porData]) => [id, new Map([...porData].map(([data, r]) => [data, r.join("; ")]))]),
  );
}

export interface OcorrenciaColaborador {
  colaboradorId: string;
  nome: string;
  // Já em "dd/mm", com sequências de dias seguidos comprimidas em "dd/mm a dd/mm".
  periodos: string[];
  // Só no dia sem horário, com o contexto: os mesmos dias, agrupados pelo motivo.
  motivos?: { periodos: string[]; motivo: string }[];
}

export interface OcorrenciasDaRegra {
  regra: string;
  rotulo: string;
  // Como resolver, quando a regra tem um caminho na tela.
  dica?: string;
  colaboradores: OcorrenciaColaborador[];
}

// Os gestos só valem na grade editável; o caminho pelo cadastro vale sempre. O reflexo do cadastro só
// alcança os rascunhos da competência atual em diante (propagarCargaPadrao).
function dicaSemHorario(editavel?: boolean) {
  const naCarga =
    "Se o horário faltar na própria carga, um Administrador de Escala pode preenchê-lo em Cargas Horárias; " +
    "os rascunhos do mês atual em diante são atualizados sozinhos.";
  return editavel
    ? "Na grade, clique com o botão direito no dia e escolha uma carga da escala que tenha horário para ele, ou dê " +
        `duplo clique para virar folga. ${naCarga}`
    : naCarga;
}

function motivosDosDias(contexto: ContextoSemHorario, colaboradorId: string, datas: string[]) {
  const porMotivo = new Map<string, string[]>();
  for (const data of [...datas].sort()) {
    const motivo = motivoSemHorario(contexto, colaboradorId, data);
    porMotivo.set(motivo, [...(porMotivo.get(motivo) ?? []), data]);
  }
  return [...porMotivo].map(([motivo, dias]) => ({ motivo, periodos: periodosDeDatas(dias) }));
}

const diaMes = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const DIA_MS = 24 * 60 * 60 * 1000;

// Datas ISO em "dd/mm"; dias corridos viram um intervalo só ("01/09 a 07/09").
export function periodosDeDatas(datas: string[]): string[] {
  const ordenadas = [...new Set(datas)].sort();
  const periodos: string[] = [];
  let i = 0;
  while (i < ordenadas.length) {
    let fim = i;
    while (fim + 1 < ordenadas.length && Date.parse(ordenadas[fim + 1]!) - Date.parse(ordenadas[fim]!) === DIA_MS) fim++;
    periodos.push(fim > i ? `${diaMes(ordenadas[i]!)} a ${diaMes(ordenadas[fim]!)}` : diaMes(ordenadas[i]!));
    i = fim + 1;
  }
  return periodos;
}

// O mínimo de gente trabalhando no dia: o do domingo, quando a geração informou, ou o de segunda a sábado.
export const minimoDoDia = (coluna: Pick<DiaColuna, "domingo">, minimo: number, minimoDomingo?: number) =>
  coluna.domingo && minimoDomingo != null ? minimoDomingo : minimo;

// "45h" ou "44h30".
export function formatarHoras(minutos: number) {
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
}

const REGRA_FOLGAS_NA_SEMANA = "FOLGAS_NA_SEMANA_DIFERENTE_DE_2";

// Como resolver, por regra; a do dia sem horário depende do contexto.
const DICA_REGRA: Record<string, string> = {
  [REGRA_FOLGAS_NA_SEMANA]:
    "No 5X2, toda semana inteira, de segunda a domingo, tem 2 folgas. O feriado em que a loja fecha, a folga " +
    "feriado (FA) e a compensação (C) não contam. A semana com ausência ou que termina no mês seguinte não é cobrada.",
};

function dicaDaRegra(regra: string, contexto?: ContextoSemHorario) {
  if (regra === REGRA_SEM_HORARIO) return contexto ? dicaSemHorario(contexto.editavel) : undefined;
  return DICA_REGRA[regra];
}

// Ocorrências do validador agrupadas por regra e, dentro dela, por colaborador (ordem alfabética).
// Com o contexto, o dia sem horário vem com o motivo de cada dia e a dica de como resolver.
export function ocorrenciasPorRegra(
  ocorrencias: Ocorrencia[],
  colaboradores: Espelho["colaboradores"],
  contexto?: ContextoSemHorario,
): OcorrenciasDaRegra[] {
  const nomes = new Map(colaboradores.map((c) => [c.id, c.nome]));
  const porRegra = new Map<string, OcorrenciaColaborador[]>();
  for (const o of ocorrencias) {
    const lista = porRegra.get(o.regra) ?? [];
    const explicar = o.regra === REGRA_SEM_HORARIO && contexto;
    lista.push({
      colaboradorId: o.colaborador_id,
      nome: nomes.get(o.colaborador_id) ?? "Colaborador não encontrado",
      periodos: periodosDeDatas(o.datas),
      ...(explicar && { motivos: motivosDosDias(contexto, o.colaborador_id, o.datas) }),
    });
    porRegra.set(o.regra, lista);
  }
  return [...porRegra].map(([regra, lista]) => {
    const dica = dicaDaRegra(regra, contexto);
    return {
      regra,
      rotulo: rotuloDaRegra(regra),
      ...(dica && { dica }),
      colaboradores: lista.sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")),
    };
  });
}

export const REGRA_DOMINGO_ACIMA_DO_TETO = "DOMINGO_ACIMA_DO_TETO";
export const REGRA_PRESENCA_ABAIXO_DO_MINIMO = "PRESENCA_ABAIXO_DO_MINIMO";
export const REGRA_FOLGAS_SEGUIDAS = "FOLGAS_SEGUIDAS";
export const REGRA_GRUPO_DE_FOLGA_POR_LEI = "GRUPO_DE_FOLGA_POR_LEI";
export const REGRA_HORAS_DA_SEMANA = "HORAS_DA_SEMANA_ACIMA_DA_JORNADA";
export const REGRA_CARGA_SEM_NINGUEM = "CARGA_SEM_NINGUEM_TRABALHANDO";
export const REGRA_FOLGA_NAO_CABE_NO_MES = "FOLGA_NAO_CABE_NO_MES";
export const REGRA_MEDIA_DE_DOMINGO_NAO_CABE = "MEDIA_DE_DOMINGO_NAO_CABE";

// rotulo é o título no modal de avisos.
const ROTULO_AVISO: Record<string, { rotulo: string; dica: string }> = {
  [REGRA_FOLGA_NAO_CABE_NO_MES]: {
    rotulo: "Folgas do mês acima do teto",
    dica:
      "Soma as folgas que a lei pede no mês (uma a cada 7 dias; no 5X2, duas por semana) e compara com o que o " +
      "teto de folga de cada dia comporta, sem os dias cheios e os feriados em que a loja fecha. Passando, algum " +
      "dia fica com mais folga que o teto. O teto é o dos Parâmetros.",
  },
  [REGRA_MEDIA_DE_DOMINGO_NAO_CABE]: {
    rotulo: "Domingos do mês acima do teto",
    dica:
      "Pela lei, cada domingo precisa em média de um terço dos homens de folga (no máximo 2 domingos trabalhados " +
      "seguidos) e de metade das mulheres (art. 386 da CLT). Com esta equipe a média passa do teto de folga do " +
      "domingo, e alguns domingos ficam acima dele. O teto do domingo é o dos Parâmetros.",
  },
  [REGRA_DOMINGO_ACIMA_DO_TETO]: {
    rotulo: "Domingo com mais folga que o teto",
    dica:
      "O gerador só passa do teto de folga do domingo quando a lei obriga a folga: o revezamento das mulheres " +
      "(art. 386 da CLT) e o limite de 2 domingos trabalhados seguidos. Uma edição manual também pode passar.",
  },
  [REGRA_PRESENCA_ABAIXO_DO_MINIMO]: {
    rotulo: "Dia com menos gente trabalhando que o mínimo",
    dica:
      "O mínimo por dia é o informado ao gerar. Feriado em que a loja fecha e o dia de folga de toda a equipe " +
      "não contam. Afastado, compensação e folga de feriado contam como fora do trabalho.",
  },
  [REGRA_FOLGAS_SEGUIDAS]: {
    rotulo: "Folgas seguidas",
    dica:
      "No 5X2 o gerador só encosta uma folga na outra quando não sobra outro dia na semana (dia cheio, grupo " +
      "estratégico, ausência). Conta só F, inclusive o domingo com a segunda.",
  },
  [REGRA_GRUPO_DE_FOLGA_POR_LEI]: {
    rotulo: "Grupo estratégico de folga junto por lei",
    dica:
      "Dois ou mais do mesmo grupo de folga no mesmo domingo, todos obrigados pela lei: a mulher que trabalhou o " +
      "domingo anterior (art. 386 da CLT) e quem chegou ao limite de domingos trabalhados seguidos. Nenhum deles " +
      "pode trabalhar nesse domingo. O gerador tenta adiantar uma das folgas antes de chegar a isso.",
  },
  [REGRA_HORAS_DA_SEMANA]: {
    rotulo: "Semana acima da jornada",
    dica:
      "Soma as horas dos dias trabalhados de segunda a domingo, sem o intervalo, contra a jornada semanal dos " +
      "Parâmetros; o que passa é hora extra. A semana que começa no mês anterior soma os dias dele, e a que " +
      "termina no mês seguinte entra quando os dias deste mês já passam. A escala 12X36 fica de fora.",
  },
  [REGRA_CARGA_SEM_NINGUEM]: {
    rotulo: "Carga horária sem ninguém trabalhando",
    dica:
      "Por carga ativa da escala, os dias em que ela tem horário e ninguém trabalha com ela, contando as trocas " +
      "de carga feitas no dia. Não entra o dia em que ninguém da escala trabalha, como o feriado em que a loja fecha.",
  },
};

// O dia e o número da regra, sem o nome dela (o modal já agrupa por ela). As folgas seguidas e o grupo de folga
// por lei dizem de quem; as contas do mês não têm dia.
function detalheDoAviso(a: Aviso, nomes: ReadonlyMap<string, string>) {
  switch (a.regra) {
    case REGRA_FOLGA_NAO_CABE_NO_MES:
      return `O mês precisa de ${a.folgas} folgas, e o teto comporta ${a.teto}`;
    case REGRA_MEDIA_DE_DOMINGO_NAO_CABE:
      return `Os domingos do mês precisam de ${a.folgas} folgas, e o teto comporta ${a.teto}`;
    case REGRA_DOMINGO_ACIMA_DO_TETO:
      return `${diaMes(a.data)}: ${a.folgas} de folga para um teto de ${a.teto}`;
    case REGRA_PRESENCA_ABAIXO_DO_MINIMO:
      return `${diaMes(a.data)}: ${a.trabalhando} trabalhando para um mínimo de ${a.minimo}`;
    case REGRA_FOLGAS_SEGUIDAS:
    case REGRA_GRUPO_DE_FOLGA_POR_LEI: {
      const nome = nomes.get(a.colaborador_id ?? "") ?? "Colaborador não encontrado";
      return `${nome}: ${periodosDeDatas(a.datas ?? [a.data]).join(", ")}`;
    }
    default:
      return diaMes(a.data);
  }
}

// Para dar nome à carga sem ninguém trabalhando.
type CargasDosAvisos = Pick<EspelhoCarga, "id" | "descricao">[];

export interface AvisosDaRegra {
  regra: string;
  rotulo: string;
  dica?: string;
  // Um por aviso, na ordem das datas.
  linhas: string[];
}

// Avisos do servidor agrupados por regra, para o modal de avisos; uma regra nova cai no próprio código.
export function avisosPorRegra(avisos: Aviso[], colaboradores: Espelho["colaboradores"] = [], cargas: CargasDosAvisos = []): AvisosDaRegra[] {
  const nomes = new Map(colaboradores.map((c) => [c.id, c.nome]));
  const porRegra = new Map<string, Aviso[]>();
  for (const a of avisos) porRegra.set(a.regra, [...(porRegra.get(a.regra) ?? []), a]);
  const linhasDa = (regra: string, lista: Aviso[]) => {
    if (regra === REGRA_HORAS_DA_SEMANA) return linhasDasHorasPorPessoa(lista, nomes);
    if (regra === REGRA_CARGA_SEM_NINGUEM) return linhasDasCargas(lista, cargas);
    return [...lista].sort((a, b) => a.data.localeCompare(b.data)).map((a) => detalheDoAviso(a, nomes));
  };
  return [...porRegra].map(([regra, lista]) => ({
    regra,
    rotulo: ROTULO_AVISO[regra]?.rotulo ?? regra,
    ...(ROTULO_AVISO[regra] && { dica: ROTULO_AVISO[regra].dica }),
    linhas: linhasDa(regra, lista),
  }));
}

// "Tarde: dias 3, 10 e 17", uma linha por carga em ordem de nome.
function linhasDasCargas(avisos: Aviso[], cargas: CargasDosAvisos) {
  const descricoes = new Map(cargas.map((c) => [c.id, c.descricao]));
  return avisos
    .map((a) => {
      const descricao = descricoes.get(a.carga_horaria_id ?? "") ?? "Carga horária não encontrada";
      const dias = (a.datas ?? [a.data]).map((d) => String(Number(d.slice(8, 10))));
      const lista = dias.length > 1 ? `${dias.slice(0, -1).join(", ")} e ${dias[dias.length - 1]}` : dias[0];
      return { descricao, linha: `${descricao}: ${dias.length > 1 ? "dias" : "dia"} ${lista}` };
    })
    .sort((a, b) => a.descricao.localeCompare(b.descricao, "pt-BR"))
    .map((c) => c.linha);
}

// As semanas acima da jornada, uma linha por pessoa em ordem de nome: quantas, a maior e os dias delas.
function linhasDasHorasPorPessoa(avisos: Aviso[], nomes: ReadonlyMap<string, string>) {
  const porPessoa = new Map<string, Aviso[]>();
  for (const a of avisos) porPessoa.set(a.colaborador_id ?? "", [...(porPessoa.get(a.colaborador_id ?? "") ?? []), a]);
  return [...porPessoa]
    .map(([id, semanas]) => {
      const nome = nomes.get(id) ?? "Colaborador não encontrado";
      const maior = formatarHoras(Math.max(...semanas.map((a) => a.minutos ?? 0)));
      const quantas = semanas.length === 1 ? `1 semana com ${maior}` : `${semanas.length} semanas, até ${maior}`;
      const dias = periodosDeDatas(semanas.flatMap((a) => a.datas ?? [a.data])).join(", ");
      return { nome, linha: `${nome}: ${quantas} para uma jornada de ${semanas[0]!.jornada_semanal ?? 0}h (${dias})` };
    })
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
    .map((p) => p.linha);
}

// Quantas linhas o modal de avisos mostra: a semana acima da jornada conta uma por pessoa.
export const quantidadeDeAvisos = (avisos: Aviso[], colaboradores: Espelho["colaboradores"] = []) =>
  avisosPorRegra(avisos, colaboradores).reduce((total, g) => total + g.linhas.length, 0);

// O que a pessoa tem a revisar: as ocorrências e os avisos com o nome dela, uma linha por regra. Erro é
// quando alguma for ocorrência, que barra a validação.
export interface Pendencia {
  erro: boolean;
  linhas: string[];
}

export type PendenciasPorColaborador = Map<string, Pendencia>;

const maiuscula = (texto: string) => texto.charAt(0).toUpperCase() + texto.slice(1);

export function pendenciasPorColaborador(ocorrencias: Ocorrencia[], avisos: Aviso[]): PendenciasPorColaborador {
  const porPessoa = new Map<string, { erro: boolean; datas: Map<string, string[]> }>();
  const juntar = (id: string, rotulo: string, datas: string[], erro: boolean) => {
    const p = porPessoa.get(id) ?? { erro: false, datas: new Map<string, string[]>() };
    p.erro ||= erro;
    p.datas.set(rotulo, [...(p.datas.get(rotulo) ?? []), ...datas]);
    porPessoa.set(id, p);
  };
  for (const o of ocorrencias) juntar(o.colaborador_id, maiuscula(rotuloDaRegra(o.regra)), o.datas, true);
  for (const a of avisos) {
    if (a.colaborador_id) juntar(a.colaborador_id, ROTULO_AVISO[a.regra]?.rotulo ?? a.regra, a.datas ?? [a.data], false);
  }
  return new Map(
    [...porPessoa].map(([id, p]) => [
      id,
      { erro: p.erro, linhas: [...p.datas].map(([rotulo, datas]) => `${rotulo}: ${periodosDeDatas(datas).join(", ")}`) },
    ]),
  );
}
